import { HttpClient } from 'core/common/services';
import { convertToBase64 } from 'core/common/utils/convertToBase64';
import { convertToBlob } from 'core/common/utils/convertToBlob';
import { stripBase64Prefix } from 'core/common/utils/stripBase64Prefix';
import { PhotoScan } from '../../entities';
import { PalmistryDetectionService } from '../../interfaces';
import { PalmDtoMapper } from '../../mappers';
import { PalmPhotoDTO } from '../dto';

export class PalmDetectionService implements PalmistryDetectionService {
  constructor(private readonly httpClient: HttpClient) {}

  async detect(image: HTMLCanvasElement): Promise<PhotoScan> {
    const dtoMapper = new PalmDtoMapper();

    const palmPhoto = new FormData();
    const imageBlob = await convertToBlob(image);

    if (imageBlob) {
      palmPhoto.append('photo', imageBlob, 'image.png');
    }

    const { data } = await this.httpClient.post<PalmPhotoDTO | { error: string }>(
      '/readings/palmistry/detect',
      {
        data: palmPhoto,
        headers: {
          'Content-type': 'multipart/form-data',
        },
      },
    );

    if ('error' in data) {
      const result = await this.detectBase64(image);
      return result;
    }

    return dtoMapper.toEntity(data);
  }

  async detectBase64(image: HTMLCanvasElement): Promise<PhotoScan> {
    const dtoMapper = new PalmDtoMapper();

    const fileString = convertToBase64(image);

    const file = stripBase64Prefix(fileString);

    const { data } = await this.httpClient.post<PalmPhotoDTO>('/readings/palmistry/detect-base64', {
      data: {
        file,
      },
    });

    return dtoMapper.toEntity(data);
  }

  async uploadPhoto(file: File): Promise<PhotoScan> {
    const dtoMapper = new PalmDtoMapper();

    const palmPhoto = new FormData();
    palmPhoto.append('photo', file, 'image.png');

    const { data } = await this.httpClient.post<PalmPhotoDTO>('/readings/palmistry/detect', {
      data: palmPhoto,
      headers: {
        'Content-type': 'multipart/form-data',
      },
    });

    if ('error' in data) {
      throw Error('Error occured uploading palm scan');
    }

    return dtoMapper.toEntity(data);
  }

  async getPalmistryScan(): Promise<PhotoScan | null> {
    const dtoMapper = new PalmDtoMapper();

    const { data } = await this.httpClient.get<PalmPhotoDTO | null>('/palmistry/scan');

    if (!data) return null;

    return dtoMapper.toEntity(data);
  }
}
